import React from 'react';

import { Box, Typography, Accordion, AccordionSummary, AccordionActions } from '@mui/material';
import { makeStyles, withStyles } from 'tss-react/mui';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

import { CalendarDateService, DateObject, formatTimeRangeWithMeridiem } from '@uplift/services';
import { Button } from '../../../Button';
import { ProviderMedicalRole } from '@uplift/types';
import { useL10n } from '@uplift/hooks';
import { RecurringList, RecurringListProps } from './RecurringList';
import { DisplayName } from '@uplift/utils';
import { AppointmentRecurring } from '@uplift/api';
import { useRecurringListQuery } from '@uplift/queries';


export type PatientRecurringSession = {
  id: string;
  recurring: AppointmentRecurring;
  isMaterialized: boolean;
  start: DateObject;
  end: DateObject;
  providerName: string;
  providerRole: ProviderMedicalRole;
  providerDisplay: DisplayName;
  connectLink?: string;
  type: 'recurring';
};

export type PatientRecurringSessionItemProps = {
  item: PatientRecurringSession;
  onCancel: (recurring: { seriesId: string; fromDatetime: string }) => void;
  onCancelItem: RecurringListProps['onSeriesItemCancelClick'];
};


const useStyles = makeStyles()(theme => ({
  timeTitle: {
    fontWeight: 600,
  },
  subtitle: {
    color: theme.palette.text.primary,
    marginTop: theme.spacing(0.5),
  },
  providerName: {
    color: theme.palette.text.secondary,
  },
  loader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
}));


const AccordionSummaryCustom = withStyles(AccordionSummary, {
  content: {
    margin: 0,
    '&.Mui-expanded': {
      margin: 0,
    }
  }
});


export const PatientRecurringSessionItem: React.FC<PatientRecurringSessionItemProps> = ({ item, onCancel, onCancelItem }) => {
  const l10n = useL10n();
  const { classes } = useStyles();
  const [ accordionOpened, setAccordionOpened ] = React.useState(false);

  const start = CalendarDateService.useDateObject(item.start);
  const end = CalendarDateService.useDateObject(item.end);

  const recurringTitle = React.useMemo(() => {
    const weekDay = start.format('dddd');

    return l10n.dashboard.appointmentRecurring[item.recurring]?.(weekDay);
  }, [ l10n, item.recurring, start ]);

  const timeRange = React.useMemo(() => {
    return formatTimeRangeWithMeridiem(start, end);
  }, [ start, end ]);

  const { data = [], isLoading } = useRecurringListQuery(item.id, 5, { enabled: accordionOpened });

  const handleCancelAll = React.useCallback(() => {
    onCancel({ seriesId: item.id, fromDatetime: data[0].fromDatetime });
  }, [ onCancel, item.id, data ]);

  const sessionType = item.providerRole === ProviderMedicalRole.psychiatrist
    ? l10n.resources.appointment.types.psychiatry
    : l10n.resources.appointment.types.therapy;

  return (
    <Box>
      <Accordion expanded={accordionOpened} onChange={(_, expanded) => setAccordionOpened(expanded)}>
        <AccordionSummaryCustom
          expandIcon={<ExpandMoreIcon />}
        >
          <Box py={3} px={4} pl={2}>
            <Box>
              <Typography variant="body1" className={classes.timeTitle}>{recurringTitle}, {timeRange}</Typography>
              <Typography variant="body1" className={classes.subtitle}>{sessionType}</Typography>
              <Typography variant="caption" className={classes.providerName}>with {item.providerName}</Typography>
            </Box>
          </Box>
        </AccordionSummaryCustom>
        <Box px={4}>
          <RecurringList
            seriesId={item.id}
            skipLoading={!accordionOpened}
            onSeriesItemCancelClick={onCancelItem}
          />
        </Box>
        <AccordionActions>
          <Button variant="text" fullWidth color="error" loading={isLoading} onClick={handleCancelAll}>Cancel All Recurring sessions</Button>
        </AccordionActions>
      </Accordion>
    </Box>
  );
};
